import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { stakingService } from '@/services/staking';
import type { DelegatorResponse, StakingInfo, ValidatorInfo } from '@/types/staking';

// Query keys
const stakingKeys = {
  all: ['staking'] as const,
  validators: () => [...stakingKeys.all, 'validators'] as const,
  validatorList: (status?: number) => [...stakingKeys.validators(), { status }] as const,
  validator: (address: string) => [...stakingKeys.validators(), address] as const,
  delegations: (address: string) => [...stakingKeys.all, 'delegations', address] as const,
  stakingInfo: () => [...stakingKeys.all, 'info'] as const,
};

/**
 * Hook to fetch all validators with optional status filter
 */
export function useValidators(status?: number): UseQueryResult<ValidatorInfo[], Error> {
  return useQuery<ValidatorInfo[], Error>({
    queryKey: stakingKeys.validatorList(status),
    queryFn: () => stakingService.getValidators(status),
    staleTime: 60_000, // 60 seconds
  });
}

/**
 * Hook to fetch a single validator by operator address
 */
export function useValidator(operatorAddress: string | undefined): UseQueryResult<ValidatorInfo, Error> {
  return useQuery<ValidatorInfo, Error>({
    queryKey: stakingKeys.validator(operatorAddress ?? ''),
    queryFn: () => stakingService.getValidator(operatorAddress!),
    enabled: operatorAddress !== undefined,
    staleTime: 60_000, // 60 seconds
  });
}

/**
 * Hook to fetch delegations for a delegator address
 */
export function useDelegations(delegatorAddress: string | undefined): UseQueryResult<DelegatorResponse, Error> {
  return useQuery<DelegatorResponse, Error>({
    queryKey: stakingKeys.delegations(delegatorAddress ?? ''),
    queryFn: () => stakingService.getDelegations(delegatorAddress!),
    enabled: delegatorAddress !== undefined,
    staleTime: Infinity, // Only invalidate on new blocks with user txs
  });
}

/**
 * Hook to fetch overall staking information
 */
export function useStakingInfo(): UseQueryResult<StakingInfo, Error> {
  return useQuery<StakingInfo, Error>({
    queryKey: stakingKeys.stakingInfo(),
    queryFn: () => stakingService.getStakingInfo(),
    staleTime: 300_000, // 5 minutes
  });
}

/**
 * Hook to invalidate staking queries
 */
export function useInvalidateStaking(): {
  invalidateValidators: () => Promise<void>;
  invalidateDelegations: (address: string) => Promise<void>;
  invalidateStakingInfo: () => Promise<void>;
  invalidateAll: () => Promise<void>;
} {
  const queryClient = useQueryClient();

  return {
    invalidateValidators: (): Promise<void> => queryClient.invalidateQueries({ queryKey: stakingKeys.validators() }),
    invalidateDelegations: (address: string): Promise<void> => 
      queryClient.invalidateQueries({ queryKey: stakingKeys.delegations(address) }),
    invalidateStakingInfo: (): Promise<void> => queryClient.invalidateQueries({ queryKey: stakingKeys.stakingInfo() }),
    invalidateAll: (): Promise<void> => queryClient.invalidateQueries({ queryKey: stakingKeys.all }),
  };
}
